import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  HStack,
  Badge,
  Spinner,
  useToast,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

const AdminPasscodes = () => {
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth('/api/authorization-codes');
      const data = await response.json();
      setCodes(data.codes || []);
    } catch (error) {
      console.error('Error fetching codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const addCode = async () => {
    if (!newCode.trim()) return;

    try {
      setAdding(true);
      const response = await fetchWithAuth('/api/authorization-codes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newCode }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast({
          title: `Code ${data.code} created for ${data.email}`,
          description: 'Share it with that person. It works for 24 hours.',
          status: 'success',
          duration: 8000,
          isClosable: true,
        });
        setNewCode('');
        fetchCodes();
      } else {
        toast({ title: data.error || 'Could not create a code', status: 'error', duration: 4000 });
      }
    } catch (error) {
      toast({
        title: 'Error adding code',
        status: 'error',
        duration: 2000,
      });
    } finally {
      setAdding(false);
    }
  };

  const deleteCode = async (codeId) => {
    try {
      const response = await fetchWithAuth(`/api/authorization-codes/${codeId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast({
          title: 'Code deleted',
          status: 'success',
          duration: 2000,
        });
        fetchCodes();
      }
    } catch (error) {
      toast({
        title: 'Error deleting code',
        status: 'error',
        duration: 2000,
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Heading size="lg" mb={6}>Passcodes</Heading>

      <HStack mb={6} maxW="480px">
        <Input
          placeholder="Email of an unfinished sign-up"
          value={newCode}
          onChange={(e) => setNewCode(e.target.value)}
          bg="white"
          onKeyPress={(e) => e.key === 'Enter' && addCode()}
        />
        <Button
          leftIcon={<AddIcon />}
          colorScheme="blue"
          onClick={addCode}
          isLoading={adding}
        >
          Create code
        </Button>
      </HStack>

      {loading ? (
        <Box textAlign="center" py={10}>
          <Spinner size="lg" />
        </Box>
      ) : codes.length === 0 ? (
        <Text color="gray.500">No passcodes created yet.</Text>
      ) : (
        <Box bg="white" borderRadius="md" overflow="hidden" boxShadow="sm">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Code</Th>
                <Th>For</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th width="60px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {codes.map((code) => (
                <Tr key={code.id}>
                  <Td fontFamily="mono">{code.code}</Td>
                  <Td fontSize="sm">{code.email || 'No account'}</Td>
                  <Td>
                    {code.used ? (
                      <Badge colorScheme="gray">Used</Badge>
                    ) : !code.email || (code.expires_at && new Date(code.expires_at) < new Date()) ? (
                      <Badge colorScheme="orange">Not usable</Badge>
                    ) : (
                      <Badge colorScheme="green">Available</Badge>
                    )}
                  </Td>
                  <Td>{formatDate(code.created_at)}</Td>
                  <Td>
                    <IconButton
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => deleteCode(code.id)}
                      aria-label="Delete code"
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default AdminPasscodes;
